import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { ArrowLeftIcon, PencilIcon, DocumentTextIcon, TrashIcon } from '@heroicons/react/24/outline'
import api from '@/services/api'
import UserEditModal from '@/components/modals/UserEditModal'
import useAuthStore from '@/stores/authStore'

export default function UserDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user: currentUser } = useAuthStore()
  const [user, setUser] = useState(null)
  const [transactions, setTransactions] = useState([])
  const [memos, setMemos] = useState([])
  const [loading, setLoading] = useState(true)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    fetchUserDetails()
  }, [id])

  const fetchUserDetails = async () => {
    try {
      setLoading(true)
      const [userResponse, transactionsResponse, memosResponse] = await Promise.all([
        api.get(`/users/${id}`),
        api.get(`/users/${id}/transactions`),
        api.get(`/users/${id}/memos?limit=50`)
      ])
      
      if (userResponse.data.success) {
        setUser(userResponse.data.data.user)
      }
      
      if (transactionsResponse.data.success) {
        setTransactions(transactionsResponse.data.data.transactions || [])
      }

      if (memosResponse.data.success) {
        setMemos(memosResponse.data.data.memos || [])
      }
    } catch (error) {
      console.error('Failed to fetch user details:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteUser = async () => {
    if (!user) return
    
    setDeleting(true)
    try {
      const response = await api.delete(`/users/${user.id}`)
      if (response.data.success) {
        alert('사용자가 성공적으로 삭제되었습니다.')
        navigate('/dashboard/users')
      }
    } catch (error) {
      console.error('Failed to delete user:', error)
      const errorMessage = error.response?.data?.error?.message || '사용자 삭제에 실패했습니다.'
      alert(errorMessage)
    } finally {
      setDeleting(false)
      setShowDeleteConfirm(false)
    }
  }

  const canDeleteUser = currentUser?.role === 'super_admin' && 
                       user && 
                       user.id !== currentUser.id && 
                       user.role !== 'super_admin'

  const formatBalance = (balance) => {
    if (typeof balance === 'string') {
      // For very large numbers returned as strings
      return parseFloat(balance).toLocaleString()
    }
    return Number(balance || 0).toLocaleString()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  if (!user) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">사용자를 찾을 수 없습니다.</p>
        <Link to="/dashboard/users" className="text-blue-600 hover:text-blue-800 mt-4 inline-block">
          사용자 목록으로 돌아가기
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard/users"
            className="text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-6 w-6" />
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">사용자 상세정보</h1>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setShowEditModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <PencilIcon className="h-5 w-5" />
            편집
          </button>
          {canDeleteUser && (
            <button 
              onClick={() => setShowDeleteConfirm(true)}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center gap-2"
            >
              <TrashIcon className="h-5 w-5" />
              삭제
            </button>
          )}
        </div>
      </div>

      {/* User Info */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">기본 정보</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">사용자명</label>
            <p className="mt-1 text-sm text-gray-900">{user.username}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">이름</label>
            <p className="mt-1 text-sm text-gray-900">{user.name}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">휴대폰 번호</label>
            <p className="mt-1 text-sm text-gray-900">{user.contact || '없음'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">역할</label>
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              user.role === 'admin' 
                ? 'bg-purple-100 text-purple-800' 
                : 'bg-green-100 text-green-800'
            }`}>
              {user.role === 'admin' ? '관리자' : '사용자'}
            </span>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">가입일</label>
            <p className="mt-1 text-sm text-gray-900">
              {new Date(user.createdAt).toLocaleDateString('ko-KR')}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">담당자코드</label>
            <p className="mt-1 text-sm text-gray-900">{user.referralCodeUsed || '없음'}</p>
          </div>
        </div>
      </div>

      {/* Balance Info */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">잔액 정보</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">원화 잔액</label>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              ₩{formatBalance(user.krwBalance)}
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">토큰 잔액</label>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {formatBalance(user.tokenBalance)}
            </p>
          </div>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">최근 거래내역</h2>
        {transactions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    유형
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    금액
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    통화
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    설명
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    지급자
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    일시
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.slice(0, 10).map((transaction) => (
                  <tr key={transaction.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {Number(transaction.amount).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {transaction.currency === 'krw' ? '원화' : '토큰'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {transaction.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {transaction.admin ? (
                        <div>
                          <div className="font-medium text-gray-900">
                            {transaction.admin.name || transaction.admin.username}
                          </div>
                          <div className="text-xs text-gray-500">
                            @{transaction.admin.username}
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">시스템</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(transaction.createdAt).toLocaleDateString('ko-KR')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">거래내역이 없습니다.</p>
        )}
      </div>

      {/* Admin Memos */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
          <DocumentTextIcon className="h-5 w-5" />
          관리자 메모 ({memos.length}개)
        </h2>
        {memos.length > 0 ? (
          <div className="h-64 overflow-y-scroll space-y-3 pr-2 border border-gray-200 rounded-lg p-3">
            {memos.map((memo) => (
              <div
                key={memo.id}
                className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-500"
              >
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm font-medium text-gray-900">
                    관리자: {memo.admin?.name || memo.admin?.username || 'Unknown'}
                  </span>
                  <span className="text-xs text-gray-500">
                    {new Date(memo.createdAt).toLocaleString('ko-KR', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
                <p className="text-sm text-gray-700 whitespace-pre-wrap break-words">
                  {memo.memo}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center py-4">작성된 메모가 없습니다.</p>
        )}
      </div>

      {/* Edit Modal */}
      <UserEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        user={user}
        onUpdate={(updatedUser) => {
          setUser(updatedUser)
          setShowEditModal(false)
        }}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                사용자 삭제 확인
              </h3>
              <p className="text-gray-600">
                정말로 <strong>{user?.name || user?.username}</strong> 사용자를 삭제하시겠습니까?
              </p>
              <p className="text-red-600 text-sm mt-2">
                이 작업은 되돌릴 수 없으며, 모든 관련 데이터가 삭제됩니다.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleDeleteUser}
                disabled={deleting}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? '삭제 중...' : '삭제'}
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 disabled:opacity-50"
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}